import { useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import {
  amendmentStatusLabel, amendmentTypeLabel, amendmentTypes, createAmendment, listAmendments, replyToAmendment,
} from '../../api/amendments';
import { formatINR } from '../data/common';

const active = (status) => ['Open', 'InProgress', 'AwaitingCustomer'].includes(status);
const when = (iso) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/**
 * Change requests for a booking made with a supplier: the customer asks, our support team replies.
 * Guests are identified by the booking email kept with the booking in this browser.
 */
export default function AmendmentPanel({ booking }) {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const supplierBookingId = booking?.supplierBookingId;
  const email = booking?.contact?.email;
  const [requests, setRequests] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supplierBookingId) return undefined;
    const controller = new AbortController();
    listAmendments(supplierBookingId, email, token, controller.signal)
      .then(setRequests)
      .catch((e) => { if (e.name !== 'AbortError') setError(e.status === 404 ? '' : e.message); });
    return () => controller.abort();
  }, [supplierBookingId, email, token]);

  if (!supplierBookingId) return null;

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      const created = await createAmendment({ supplierBookingId, email, type: form.type, message: form.message.trim() }, token);
      setRequests((list) => [created, ...(list || [])]);
      setForm(null);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const replaced = (updated) => setRequests((list) => list.map((r) => (r.id === updated.id ? updated : r)));

  return (
    <section className="panel amend-panel" id="support">
      <h3>Changes &amp; support</h3>
      <p className="muted small">Need to cancel, change a date or fix a name? Send a request — our team checks with the {booking.supplierCode ? 'supplier' : 'provider'} and replies here and by email, usually within a few hours.</p>
      {error && <p className="api-message error" role="alert">{error}</p>}

      {!form && <button type="button" className="btn btn-outline" onClick={() => setForm({ type: 'DateChange', message: '' })}>Request a change</button>}
      {form && (
        <form className="amend-form" onSubmit={submit}>
          <label className="field"><span className="small">What do you need?</span>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {amendmentTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="field"><span className="small">Details</span>
            <textarea rows="4" required minLength="5" maxLength="2000" value={form.message}
              placeholder="E.g. Please move my flight to 12 Nov, same time if possible."
              onChange={(e) => setForm({ ...form, message: e.target.value })} />
          </label>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setForm(null)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Sending…' : 'Send request'}</button>
          </div>
        </form>
      )}

      {requests?.map((r) => <RequestItem key={r.id} request={r} email={email} token={token} onUpdated={replaced} />)}
    </section>
  );
}

function RequestItem({ request: r, email, token, onUpdated }) {
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const send = async (event) => {
    event.preventDefault();
    setBusy(true); setError('');
    try { onUpdated(await replyToAmendment(r.id, reply.trim(), email, token)); setReply(''); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="amend-item">
      <div><b>{amendmentTypeLabel[r.type] || r.type}</b> · <span className={`status-pill${r.status === 'Completed' ? ' live' : r.status === 'Rejected' ? ' cancelled' : ''}`}>{amendmentStatusLabel[r.status] || r.status}</span></div>
      {r.charges != null && <p className="small">Charges for this change: <b>{formatINR(r.charges)}</b></p>}
      <div className="amend-thread">
        <div className="amend-msg customer"><span className="muted small">You · {when(r.createdAt)}</span><p>{r.message}</p></div>
        {r.messages.map((m) => (
          <div key={m.id} className={`amend-msg ${m.fromAdmin ? 'admin' : 'customer'}`}>
            <span className="muted small">{m.fromAdmin ? m.authorName : 'You'} · {when(m.createdAt)}</span><p>{m.text}</p>
          </div>
        ))}
      </div>
      {active(r.status) && (
        <form className="amend-form" onSubmit={send}>
          {error && <p className="api-message error" role="alert">{error}</p>}
          <textarea rows="2" required maxLength="2000" placeholder={r.status === 'AwaitingCustomer' ? 'Reply to support…' : 'Add more details…'} value={reply} onChange={(e) => setReply(e.target.value)} />
          <div><button type="submit" className="btn btn-outline btn-sm" disabled={busy || !reply.trim()}>{busy ? 'Sending…' : 'Send reply'}</button></div>
        </form>
      )}
    </div>
  );
}
